<template>
  <div class="mb-4">
    <!-- Header -->
    <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
      <div class="text-black text-2xl font-semibold">
        Information
      </div>

      <div class="month-picker w-[130px] border flex justify-between rounded items-center px-[5px] bg-white">
        <MonthlyPicker
          v-model="selectedMonth"
          date-format="MMM YYYY"
          style="width: 90px"
          input-class="border-0 w-[100px] h-full font-semibold"
          :clear-option="false"
          :month-labels="monthlabel"
        />
        <span class="k-calendar text-3xl" />
      </div>
    </div>

    <div class="d-flex flex-wrap -mx-[8px] mt-3">
      <!-- Summary Cards -->
      <div
        v-for="card in summaryCards"
        :key="card.label"
        class="w-full sm:w-1/2 md:w-1/3 xl:w-1/5 p-[8px] pb-[16px]"
      >
        <div class="border rounded px-2 py-3 h-100 d-flex flex-column bg-white shadow-sm">
          <div class="d-flex align-items-center mb-2 min-h-[34px]">
            <div
              v-if="card.label === 'Total Partner Aktif'"
              class="p-[5px] bg-[#DFF3FF] rounded mr-1 flex-shrink-0"
            >
              <span class="k-profile-2user text-[#08A0F7] text-xl" />
            </div>

            <span
              class="text-black text-[14px] leading-tight"
              v-html="card.label"
            />
          </div>

          <div class="d-flex align-items-center flex-wrap">
            <span class="text-black text-2xl lg:text-3xl font-semibold">
              {{ card.value }}
            </span>

            <span
              v-if="card.badge"
              class="ml-2 px-1 py-[2px] text-md rounded-xl bg-[#DCF3EB] text-[#34A770]"
            >
              +{{ card.badge }}
            </span>
          </div>
          <div
            v-if="card.label === 'Total Partner Aktif'"
            class="flex gap-[10px] mt-1 text-sm"
          >
            <div class="flex flex-col gap-1">
              <span>Saudagar</span>
              <span>Juragan</span>
            </div>

            <div class="flex flex-col gap-1">
              <span>: {{ card.saudagar }}</span>
              <span>: {{ card.juragan }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Product Cards -->
      <b-collapse
        id="collapse-product-cards"
        v-model="isExpanded"
        class="w-100"
      >
        <div class="d-flex flex-wrap -mx-[8px]">
          <div
            v-for="product in productsList"
            :key="product.key"
            class="w-full sm:w-1/2 md:w-1/3 xl:w-1/5 p-[8px] pb-[16px]"
          >
            <div class="border rounded px-2 py-3 d-flex flex-column bg-white shadow-sm h-100">
              <div class="d-flex align-items-center mb-2 min-h-[34px]">
                <img
                  :src="product.logo"
                  :alt="product.label"
                  class="mr-1 w-[24px] h-[24px] object-contain flex-shrink-0"
                >

                <span class="text-black text-[14px] leading-tight">
                  {{ product.label }}
                </span>
              </div>

              <span class="text-black text-2xl lg:text-3xl font-semibold">
                {{ product.value }}
              </span>

              <!-- Saudagar / Juragan -->
              <div
                v-if="showPartnerLevel(product.key)"
                class="flex gap-[10px] mt-1 text-sm"
              >
                <div class="flex flex-col gap-1">
                  <span>Saudagar</span>
                  <span>Juragan</span>
                </div>

                <div class="flex flex-col gap-1">
                  <span>: {{ (product.total_partner_levels && product.total_partner_levels.saudagar) || 0 }}</span>
                  <span>: {{ (product.total_partner_levels && product.total_partner_levels.juragan) || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-collapse>
    </div>

    <!-- Toggle -->
    <div class="d-flex justify-content-center mt-2 mb-3">
      <button
        v-b-toggle.collapse-product-cards
        class="btn btn-link text-black d-flex align-items-center shadow-none text-decoration-none"
      >
        {{ isExpanded ? 'Lebih Sedikit' : 'Lihat Selengkapnya' }}

        <span
          :class="isExpanded ? 'k-arrow-up-2' : 'k-arrow-down-1'"
          class="ml-1"
        />
      </button>
    </div>
  </div>
</template>

<script>
import MonthlyPicker from 'vue-monthly-picker'
import { LABELMONTH } from '@/libs/filterDate'

export default {
  components: {
    MonthlyPicker,
  },

  props: {
    totalPartner: {
      type: Number,
      default: 0,
    },
    newPartner: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      selectedMonth: this.$moment(),
      monthlabel: LABELMONTH,
      isExpanded: false,
      dashboardData: {},
    }
  },

  computed: {
    summaryCards() {
      const d = this.dashboardData

      return [
        {
          label: 'Total Partner Aktif',
          value: d.total_active_partners || 0,
          badge: this.newPartner || 0,
          saudagar: d.total_active_partner_levels?.saudagar || 0,
          juragan: d.total_active_partner_levels?.juragan || 0,
        },
        {
          label: 'Partner ≥ <b>4</b> Produk',
          value: d.partner_more_than_4 + d.partner_4_products || 0,
        },
        {
          label: 'Partner <b>3</b> Produk',
          value: d.partner_3_products || 0,
        },
        {
          label: 'Partner <b>2</b> Produk',
          value: d.partner_2_products || 0,
        },
        {
          label: 'Partner <b>1</b> Produk',
          value: d.partner_1_product || 0,
        },
      ]
    },

    productsList() {
      const d = this.dashboardData

      if (!d || Object.keys(d).length === 0) {
        return []
      }

      const excludeKeys = [
        'period',
        'total_active_partners',
        'total_active_partner_levels',
        'partner_1_product',
        'partner_2_products',
        'partner_3_products',
        'partner_4_products',
        'partner_more_than_4',
      ]

      const formatLabel = key => {
        const labels = {
          ro: 'Partner Raja Ongkir',
          komcard: 'Partner Komcards',
          komads: 'Partner KomAds',
        }

        return labels[key] || `Partner ${key.charAt(0).toUpperCase() + key.slice(1)}`
      }

      const products = Object.entries(d)
        .filter(([key]) => !excludeKeys.includes(key))
        .map(([key, item]) => ({
          key,
          label: item.name || formatLabel(key),
          logo: item.logo || '',
          value: item.count || 0,
          total_partner_levels: item.total_partner_levels || {},
        }))

      products.push({
        key: 'total',
        label: 'Total Partner Terdaftar',
        logo: 'https://storage.googleapis.com/komerce/assets/icons/icon-jumlah-partner.svg',
        value: this.totalPartner,
      })

      return products
    },
  },

  watch: {
    selectedMonth() {
      this.fetchDashboardData()
    },
  },

  mounted() {
    this.fetchDashboardData()
  },

  methods: {
    showPartnerLevel(productKey) {
      const excludedKeys = [
        'ro',
        'komchat',
        'total',
      ]

      return !excludedKeys.includes(productKey)
    },

    async fetchDashboardData() {
      try {
        const { data } = await this.$http_new.get(
          '/auth/api/v1/admin/partner-active/dashboard',
          {
            params: {
              period: this.selectedMonth
                ? this.$moment(this.selectedMonth).format('YYYY-MM')
                : '',
            },
          },
        )

        if (data?.data) {
          this.dashboardData = data.data
        }
      } catch (e) {
        this.$toast_error({
          message: 'Gagal mengambil data partner aktif',
        })
      }
    },
  },
}
</script>

<style scoped>
::v-deep .vue-monthly-picker .date-popover {
  left: -75px !important;
  z-index: 1050;
}

@media (max-width: 576px) {
  ::v-deep .vue-monthly-picker .date-popover {
    left: auto !important;
    right: 0 !important;
  }
}
</style>
